import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "../context/UserContext";
import {
  applyJourneyOrgToAuthorizationParams,
  applyJourneyOrgToAuthorizationParamsWithClientFallback,
  getApplicationClientIdFromIdTokenClaims,
  getConfig,
} from "../config";
import waveImage from "../assets/background-bottom.png";
import decorativeImage from "../assets/background-right.png";

const Home = () => {
  const { selectedJourney, setSelectedJourney, selectedLocale, getClientConfig } = useUser();
  const { loginWithRedirect, isAuthenticated, getIdTokenClaims } = useAuth0();
  const { search } = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(search);

  // Handle error query param once on mount/when query changes
  useEffect(() => {
    if (params.has("error")) {
      alert(`Oops... ${params.get("error_description") || params.get("error")}. Redirecting back to login.`);
      navigate(`/login?journey=${encodeURIComponent(selectedJourney || "")}`, { replace: true });
    }
  }, [search, selectedJourney, navigate]);

  const handleGetStarted = async () => {
    if (!selectedJourney) {
      alert("Please select a journey first");
      return;
    }
    
    // Pass through all URL params to authorization params
    const params = new URLSearchParams(search);
    const authorizationParams = { ui_locales: selectedLocale };
    
    // Pass through all URL params except error-related and journey params
    params.forEach((value, key) => {
      if (key !== 'error' && key !== 'error_description' && key !== 'journey') {
        authorizationParams[key] = value;
      }
    });

    await loginWithRedirect({
      authorizationParams: applyJourneyOrgToAuthorizationParams(
        authorizationParams,
        getClientConfig()
      ),
    });
  };

  const handleProfile = async () => {
    let applicationClientId;
    try {
      applicationClientId = getApplicationClientIdFromIdTokenClaims(
        await getIdTokenClaims()
      );
    } catch {
      applicationClientId = undefined;
    }
    const appConfig = getConfig();
    await loginWithRedirect({
      authorizationParams: applyJourneyOrgToAuthorizationParamsWithClientFallback(
        {
          ui_locales: selectedLocale,
          "ext-manage-profile": true,
        },
        getClientConfig(),
        appConfig.journeyConfig,
        applicationClientId
      ),
    });
  };

  if (isAuthenticated) {
    return (
      <div className="landing-page">
        <div className="landing-content">
          <h1>You're Logged In!</h1>
          <p>
            Navigate to your profile to edit your details, change a<br />
            password, and/or view other options
          </p>
          <button className="profile-btn" onClick={handleProfile}>
            GO TO MY PROFILE
          </button>
        </div>
        <img src={decorativeImage} alt="" className="decorative-image" />
        <img src={waveImage} alt="" className="wave-image" />
      </div>
    );
  }

  return (
    <div className="landing-page">
      <div className="landing-content">
        <h1>
          Welcome to the Shell<br />
          CIPM Demo Hub!
        </h1>

        <p>
          This demo application will let you test the various features<br />
          and user journeys of the CIPM Customer Identity Journeys.<br />
          Select a user journey in the dropdown to get started
        </p>

        <div className="journey-selector">
          <label htmlFor="journey-options">Select a user journey</label>
          <select
            id="journey-options"
            name="journeyOptions"
            value={selectedJourney || ""}
            onChange={(e) => setSelectedJourney(e.target.value)}
          >
            <option value="" disabled>
              -- Choose a journey --
            </option>
            <option value="email_password_ig_new_1">Email+password - IG - New 1</option>
            <option value="email_password_ig_new_2">Email+password - IG - New 2</option>
            <option value="email_password_ig_new_3">Email+password - IG - New 3</option>
            <option value="email_password_ig_new_4">Email+password - IG - New 4</option>  
            <option value="email_password_2sv_optional_new_1">Email+password - 2SV optional - New 1</option>
            <option value="PBS00355237DemoAppEmailpasswordB2B">Demo App Email+password - B2B</option>
            <option value="PBS00000000DataSyncEmailpasswordB2B">Data Sync Email+password - B2B</option>
            <option value="email_password_2sv_disabled_new_1">Email+password - 2SV disabled - New 1</option>
            <option value="mobile_password_ig_new_1">Mobile+password - IG - New 1</option>
            <option value="mobile_password_2sv_disabled_new_1">Mobile+password - 2SV disabled - New 1</option>
            <option value="mobile+pwdB2B">Mobile+password - B2B</option>
            <option value="mobile_otp_ig_new_1">Mobile+OTP - IG - New 1</option>
            <option value="shell_recharge">Shell Recharge</option>
            <option value="progressive_profiling">Progressive Profiling</option>
            <option value="PBS00001000DataSyncEmailpasswordB2BExcludeLA">Data Sync Email+password - B2B - Exclude LA</option>
            <option value="PBS00001001DataSyncEmailpasswordB2BDiffLA">Data Sync Email+password - B2B - Diff LA</option>
            <option value="email_password_2sv_optional_diff_la">Email+password - 2SV optional - Diff LA</option>
          </select>
        </div>

        <button className="start-btn" onClick={handleGetStarted}>
          GET STARTED
        </button>
      </div>

      <img src={decorativeImage} alt="" className="decorative-image" />
      <img src={waveImage} alt="" className="wave-image" />
    </div>
  );
};

export default Home;
